export type ExecuteAggRequestArgs = {
	url: string;
	headers: Record<string, string>;
	body: unknown;
	timeoutMs?: number;
	/** Optional external signal to abort the request (e.g. when the user clicks Undo). */
	signal?: AbortSignal;
	throwOnError?: boolean;
	customerErrors?: boolean;
};

const getResponseErrorMessage = (responseBody: unknown, status: number): string => {
	if (typeof responseBody === 'string') {
		return responseBody.trim() || `Request failed with status ${status}.`;
	}
	if (responseBody && typeof responseBody === 'object') {
		const { message, error } = responseBody as { message?: unknown; error?: unknown };
		if (typeof message === 'string' && message.trim()) {
			return message;
		}
		if (typeof error === 'string' && error.trim()) {
			return error;
		}
	}
	return `Request failed with status ${status}.`;
};

export async function executeAggRequest(args: ExecuteAggRequestArgs): Promise<any> {
	const {
		url,
		headers,
		body,
		timeoutMs = 30000,
		signal: externalSignal,
		throwOnError = false,
		customerErrors = false,
	} = args;

	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), timeoutMs);

	// If an external signal is provided, abort the internal controller when it fires.
	let onExternalAbort: (() => void) | undefined;
	if (externalSignal) {
		if (externalSignal.aborted) {
			controller.abort();
		} else {
			onExternalAbort = () => controller.abort();
			externalSignal.addEventListener('abort', onExternalAbort);
		}
	}

	try {
		const res = await fetch(url, {
			method: 'POST',
			credentials: 'include',
			headers,
			body: JSON.stringify(body),
			signal: controller.signal,
		});

		const ct = res.headers.get('content-type') || '';
		const requestId = res.headers.get('x-request-id') ?? undefined;
		let responseBody;
		try {
			responseBody = ct.includes('application/json') ? await res.json() : await res.text();
		} catch (error) {
			if (!customerErrors || controller.signal.aborted) throw error;
			return { error: 'Invalid response', statusCode: res.status, requestId };
		}
		if (
			customerErrors &&
			(!responseBody ||
				typeof responseBody !== 'object' ||
				(!(Array.isArray(responseBody.errors) && responseBody.errors.length > 0) &&
					(!res.ok || !('data' in responseBody))))
		) {
			return { error: 'Request failed', statusCode: res.status, requestId };
		}
		if (customerErrors && responseBody) {
			return { ...responseBody, statusCode: res.status, requestId };
		}
		if (throwOnError && !res.ok) {
			const error = new Error(getResponseErrorMessage(responseBody, res.status)) as Error & {
				statusCode: number;
			};
			error.statusCode = res.status;
			throw error;
		}
		return responseBody;
	} finally {
		clearTimeout(timeout);
		if (onExternalAbort && externalSignal) {
			externalSignal.removeEventListener('abort', onExternalAbort);
		}
	}
}

export function buildRequestPreview(args: {
	url: string;
	headers: Record<string, string>;
	body: any;
}): {
	method: 'POST';
	url: string;
	headers: Record<string, string>;
	credentials: 'include';
	body: any;
} {
	const { url, headers, body } = args;
	return {
		method: 'POST' as const,
		url,
		headers,
		credentials: 'include' as const,
		body,
	};
}

export function extractOperationName(query: string): string | undefined {
	const match = query.match(/(?:query|mutation)\s+(\w+)/);
	return match ? match[1] : undefined;
}
